import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import {
  Gift, ChevronRight, ChevronLeft, Check, Mail, Clock, Crown, Star,
  Gem, Sparkles, Loader2, Copy, ArrowRight, Eye, EyeOff, Send,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const TIER_META = {
  omni_plus: { icon: Star, gradient: "from-amber-500 to-yellow-400", accent: "text-amber-600 dark:text-amber-400" },
  duo: { icon: Gem, gradient: "from-cyan-500 to-blue-500", accent: "text-cyan-600 dark:text-cyan-400" },
  platinum: { icon: Crown, gradient: "from-purple-500 to-pink-500", accent: "text-purple-600 dark:text-purple-400" },
  diamond: { icon: Sparkles, gradient: "from-violet-500 to-indigo-500", accent: "text-violet-600 dark:text-violet-400" },
};

const GIFT_TEMPLATES = [
  { emoji: "🎂", label: "Birthday", text: "Happy birthday! Here's a gift that keeps giving — your own AI companion to guide, inspire, and grow with you. Wishing you wisdom and joy this year." },
  { emoji: "🎓", label: "Graduation", text: "Congratulations on graduating! As you step into the next chapter, may Omni be the companion that helps you navigate every new challenge with confidence." },
  { emoji: "💛", label: "Thank You", text: "You've been there for me more times than I can count. This is my way of saying thank you — an AI companion as thoughtful and caring as you are." },
  { emoji: "🌟", label: "Just Because", text: "No special occasion needed — you deserve something special. Meet Omni, an AI that actually listens, remembers, and cares. Just like you do for others." },
  { emoji: "🤗", label: "Get Well", text: "Thinking of you and sending healing energy your way. Omni has mood tracking, guided therapy paths, and a calming presence — take your time, feel better." },
  { emoji: "💪", label: "Encouragement", text: "You've got this. For the moments when you need a pep talk, Omni's Life Coach and Mentor are here. Believe in yourself — the way I believe in you." },
  { emoji: "🏡", label: "New Chapter", text: "New home, new city, new adventure! Wherever life takes you, Omni will be right there — in 113 languages, in every timezone, always on your side." },
  { emoji: "🎄", label: "Holidays", text: "This holiday season, I wanted to give you something truly meaningful — an AI companion that respects your traditions, speaks your language, and never forgets." },
];

export default function GiftPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [step, setStep] = useState(1);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState("1_month");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [note, setNote] = useState("");
  const [anonymous, setAnonymous] = useState(false);

  // Result
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const { data } = await axios.get(`${API}/gift/options`, { headers });
        setOptions(data);
        const tierKeys = Object.keys(data.tiers);
        if (tierKeys.length > 0) setSelectedTier(tierKeys[0]);
      } catch {
        toast.error("Failed to load gift options");
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const getAmount = () => {
    if (!options || !selectedTier || !selectedDuration) return 0;
    const tier = options.tiers[selectedTier];
    const dur = { "1_month": 1, "3_months": 3, "6_months": 5.5, "12_months": 10 };
    return (tier.price_monthly * (dur[selectedDuration] || 1)).toFixed(2);
  };

  const emailValid = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(recipientEmail);
  const isSelfGift = recipientEmail.toLowerCase() === (user?.email || "").toLowerCase();

  const canProceed = () => {
    if (step === 1) return !!selectedTier;
    if (step === 2) return !!selectedDuration;
    if (step === 3) return emailValid && !isSelfGift;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/gift/create`, {
        tier: selectedTier,
        duration: selectedDuration,
        recipient_email: recipientEmail.trim(),
        note: note.trim(),
        anonymous,
        origin_url: window.location.origin,
      }, { headers });

      if (data.status === "redirect" && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setResult(data);
        setStep(5);
        toast.success("Gift created successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create gift");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Gift code copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 overflow-y-auto">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center mb-3">
              <Gift className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">Gift a Subscription</h1>
            <p className="text-sm text-muted-foreground mt-1">Give someone the gift of Omni</p>
            <button data-testid="bulk-gift-link" onClick={() => navigate("/gift/bulk")} className="text-xs text-primary hover:underline mt-1">
              Sending to a team? Try Bulk Gift
            </button>
          </div>

          {/* Progress Steps */}
          {step <= 4 && (
            <div data-testid="gift-steps" className="flex items-center justify-center gap-2 mb-8">
              {["Tier", "Duration", "Recipient", "Review"].map((label, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold transition-all",
                    step > i + 1 ? "bg-primary text-primary-foreground" :
                    step === i + 1 ? "bg-primary text-primary-foreground ring-2 ring-primary/30" :
                    "bg-muted text-muted-foreground"
                  )}>
                    {step > i + 1 ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={cn("text-[11px] hidden sm:block", step === i + 1 ? "font-semibold text-foreground" : "text-muted-foreground")}>{label}</span>
                  {i < 3 && <ChevronRight className="w-3 h-3 text-muted-foreground/50 hidden sm:block" />}
                </div>
              ))}
            </div>
          )}

          {/* Step 1: Choose Tier */}
          {step === 1 && options && (
            <div data-testid="gift-step-tier" className="space-y-3 animate-in fade-in">
              <p className="text-sm font-medium text-center mb-4">Choose a plan to gift</p>
              {Object.entries(options.tiers).map(([key, tier]) => {
                const meta = TIER_META[key] || TIER_META.omni_plus;
                const Icon = meta.icon;
                return (
                  <button
                    key={key}
                    data-testid={`gift-tier-${key}`}
                    onClick={() => setSelectedTier(key)}
                    className={cn(
                      "w-full p-4 rounded-2xl border-2 transition-all text-left flex items-start gap-4",
                      selectedTier === key
                        ? "border-primary bg-primary/5"
                        : "border-border/50 hover:border-primary/30 bg-card"
                    )}
                  >
                    <div className={cn("w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center flex-shrink-0", meta.gradient)}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{tier.name}</span>
                        <span className="text-xs text-muted-foreground">${tier.price_monthly}/mo</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {tier.features.slice(0, 3).map((f, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground">{f}</span>
                        ))}
                      </div>
                    </div>
                    {selectedTier === key && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-primary-foreground" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 2: Choose Duration */}
          {step === 2 && options && (
            <div data-testid="gift-step-duration" className="space-y-3 animate-in fade-in">
              <p className="text-sm font-medium text-center mb-4">How long should the gift last?</p>
              {Object.entries(options.durations).map(([key, dur]) => (
                <button
                  key={key}
                  data-testid={`gift-duration-${key}`}
                  onClick={() => setSelectedDuration(key)}
                  className={cn(
                    "w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between",
                    selectedDuration === key
                      ? "border-primary bg-primary/5"
                      : "border-border/50 hover:border-primary/30 bg-card"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{dur.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg">${getAmountForDuration(key)}</span>
                    {key !== "1_month" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                        Save {getSavingsPercent(key)}%
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 3: Recipient & Note */}
          {step === 3 && (
            <div data-testid="gift-step-recipient" className="space-y-5 animate-in fade-in">
              <div>
                <label className="text-sm font-medium mb-2 block">Recipient email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    data-testid="gift-recipient-email"
                    type="email"
                    value={recipientEmail}
                    onChange={e => setRecipientEmail(e.target.value)}
                    placeholder="friend@example.com"
                    className="w-full pl-10 pr-4 py-3 rounded-2xl border border-border/50 bg-card text-sm focus:outline-none focus:border-primary"
                  />
                </div>
                {recipientEmail && !emailValid && (
                  <p className="text-[11px] text-red-500 mt-1">Please enter a valid email address</p>
                )}
                {isSelfGift && (
                  <p className="text-[11px] text-red-500 mt-1">You cannot gift a subscription to yourself</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Personal note <span className="text-muted-foreground font-normal">(optional)</span></label>
                <div className="mb-3">
                  <p className="text-[11px] text-muted-foreground mb-2">Quick templates — tap to use</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="gift-templates">
                    {GIFT_TEMPLATES.map(t => (
                      <button
                        key={t.label}
                        data-testid={`gift-template-${t.label.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => setNote(t.text)}
                        className={cn(
                          "px-2.5 py-1.5 rounded-xl border text-[11px] transition-all hover:border-primary/40 hover:bg-primary/5",
                          note === t.text ? "border-primary/40 bg-primary/5 font-medium" : "border-border/50 bg-card"
                        )}
                      >
                        {t.emoji} {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  data-testid="gift-note"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Write a heartfelt message..."
                  className="w-full px-4 py-3 rounded-2xl border border-border/50 bg-card text-sm focus:outline-none focus:border-primary resize-none"
                />
                <p className="text-[10px] text-muted-foreground text-right mt-0.5">{note.length}/500</p>
              </div>

              <button
                data-testid="gift-anonymous-toggle"
                onClick={() => setAnonymous(!anonymous)}
                className={cn(
                  "flex items-center gap-3 w-full p-3 rounded-2xl border transition-all",
                  anonymous ? "border-primary/40 bg-primary/5" : "border-border/50 bg-card"
                )}
              >
                {anonymous ? <EyeOff className="w-4 h-4 text-primary" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                <div className="text-left">
                  <p className="text-sm font-medium">Send anonymously</p>
                  <p className="text-[11px] text-muted-foreground">{anonymous ? "Your name will be hidden from the recipient" : "The recipient will see your name"}</p>
                </div>
              </button>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && options && (
            <div data-testid="gift-step-review" className="space-y-4 animate-in fade-in">
              <p className="text-sm font-medium text-center mb-2">Review your gift</p>
              <div className="bg-card border border-border/50 rounded-3xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Plan</span>
                  <span className="text-sm font-semibold">{options.tiers[selectedTier]?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Duration</span>
                  <span className="text-sm font-semibold">{options.durations[selectedDuration]?.label}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Recipient</span>
                  <span className="text-sm font-semibold">{recipientEmail}</span>
                </div>
                {note && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Note</span>
                    <p className="text-xs bg-muted/30 rounded-xl p-2.5 italic">&ldquo;{note}&rdquo;</p>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Anonymous</span>
                  <span className="text-sm">{anonymous ? "Yes" : "No"}</span>
                </div>
                <div className="border-t border-border/30 pt-3 flex items-center justify-between">
                  <span className="text-sm font-medium">Total</span>
                  <span className="text-xl font-bold">${getAmount()}</span>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground text-center">
                Gift expires 7 days after purchase if not redeemed. Only the recipient can redeem it.
              </p>
            </div>
          )}

          {/* Step 5: Success */}
          {step === 5 && result && (
            <div data-testid="gift-success" className="text-center space-y-6 animate-in fade-in zoom-in-95">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                <Gift className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold mb-1">Gift Created!</h2>
                <p className="text-sm text-muted-foreground">
                  {result.tier_name} — {result.duration_label}
                </p>
              </div>
              <div className="bg-card border border-border/50 rounded-2xl p-4">
                <p className="text-[11px] text-muted-foreground mb-2">Gift Code</p>
                <div className="flex items-center gap-2 justify-center">
                  <code data-testid="gift-code-display" className="text-xl font-mono font-bold tracking-wider text-primary">
                    {result.gift_code}
                  </code>
                  <button
                    data-testid="copy-gift-code-btn"
                    onClick={() => copyCode(result.gift_code)}
                    className={cn(
                      "p-2 rounded-lg transition-all",
                      copied ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600" : "bg-muted/50 text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Share this code with {recipientEmail}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="gift-another-btn"
                  onClick={() => { setStep(1); setResult(null); setRecipientEmail(""); setNote(""); }}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Gift Another
                </Button>
                <Button
                  onClick={() => navigate("/chat")}
                  className="flex-1 rounded-full bg-primary text-primary-foreground"
                >
                  Back to Chat
                </Button>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {step <= 4 && (
            <div className="flex gap-3 mt-8">
              {step > 1 && (
                <Button
                  data-testid="gift-back-btn"
                  variant="outline"
                  onClick={() => setStep(s => s - 1)}
                  className="flex-1 rounded-full"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              {step < 4 && (
                <Button
                  data-testid="gift-next-btn"
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canProceed()}
                  className="flex-1 rounded-full bg-primary text-primary-foreground disabled:opacity-40"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {step === 4 && (
                <Button
                  data-testid="gift-checkout-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 text-white"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  {submitting ? "Processing..." : `Pay $${getAmount()}`}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );

  function getAmountForDuration(durKey) {
    if (!options || !selectedTier) return "0.00";
    const tier = options.tiers[selectedTier];
    const mults = { "1_month": 1, "3_months": 3, "6_months": 5.5, "12_months": 10 };
    return (tier.price_monthly * (mults[durKey] || 1)).toFixed(2);
  }

  function getSavingsPercent(durKey) {
    const mults = { "3_months": 3, "6_months": 5.5, "12_months": 10 };
    const months = { "3_months": 3, "6_months": 6, "12_months": 12 };
    const m = mults[durKey] || 1;
    const mo = months[durKey] || 1;
    return Math.round((1 - m / mo) * 100);
  }
}
